import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Infinity } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import Magnetic from "@/components/Magnetic";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="site-navbar"
    >
      <div
        className={`max-w-7xl mx-auto px-4 md:px-6 transition-all duration-500 ${
          scrolled ? "" : ""
        }`}
      >
        <nav
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-all duration-500 ${
            scrolled
              ? "glass shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]"
              : "bg-transparent border border-transparent"
          }`}
        >
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FF4500] text-black">
              <Infinity className="h-5 w-5" strokeWidth={3} />
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#CCFF00]" />
            </span>
            <span className="font-display text-xl tracking-tight">
              inkaa<span className="text-[#FF4500]">.space</span>
            </span>
          </Link>

          <ul className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `relative px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive
                        ? "text-white bg-white/8"
                        : "text-white/70 hover:text-white"
                    }`
                  }
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="hidden md:flex items-center gap-3">
            <Magnetic strength={0.3}>
              <Link
                to="/contact"
                className="btn-primary !py-2.5 !px-5 !text-xs"
                data-testid="navbar-cta-button"
              >
                Start a project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Magnetic>
          </div>

          <button
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/15 text-white"
            onClick={() => setOpen((s) => !s)}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            name="mobile-menu-toggle"
            data-testid="mobile-menu-toggle"
          >
            <span className="sr-only">{open ? "Close navigation menu" : "Open navigation menu"}</span>
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </nav>
      </div>

      {open && (
        <div
          className="md:hidden absolute inset-x-4 top-20 glass rounded-3xl p-6 shadow-xl"
          data-testid="mobile-menu"
        >
          <ul className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-2xl text-base font-medium transition-colors ${
                      isActive ? "bg-white/8 text-white" : "text-white/80"
                    }`
                  }
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <Magnetic strength={0.2}>
            <Link
              to="/contact"
              className="btn-primary w-full justify-center mt-4"
              data-testid="mobile-cta-button"
            >
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Magnetic>
        </div>
      )}
    </header>
  );
}
